// Globals
import React, { ReactNode, useCallback } from 'react';
import type { UploadProps } from 'antd';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';

// Components
import { StyledDragger } from './PureImageUploaderStyle';
import { showErrorToast } from '@/components';

// Helpers
import { validateFileSize, validateImageResolution } from '@/utils';

interface IPureImageUploader extends Omit<UploadProps, 'onChange'> {
  acceptance?: string;
  imageMaxSize: number;
  imageMaxWidth?: number;
  maxCount?: number;
  imageMaxHeight?: number;
  showUploadList?: boolean;
  onChange?: (files: File[]) => void;
  handleShowError?: (hasError: boolean) => void;
  children: ReactNode;
}

const PureImageUploader = ({
  onChange,
  acceptance,
  imageMaxSize,
  imageMaxWidth,
  imageMaxHeight,
  maxCount = 1,
  showUploadList = true,
  handleShowError,
  children,
  ...props
}: IPureImageUploader) => {
  const { t } = useTranslation(['common']);

  const handleSelectFiles = useCallback(
    debounce(async (files: File[] | Blob[]) => {
      handleShowError?.(false);
      const validFiles: File[] = [];

      await Promise.all(
        files.map(
          (file) =>
            new Promise<void>((resolve) => {
              const sizeIsValid = validateFileSize(file.size, imageMaxSize);
              const formatIsValid = acceptance?.includes(file.type);

              if (!formatIsValid) {
                showErrorToast({ message: t('error_file_format') });
                resolve();
                return;
              }

              if (!sizeIsValid) {
                showErrorToast({
                  message: t('error_file_size', {
                    limit: imageMaxSize / 1024 / 1024,
                  }),
                });
                resolve();
                return;
              }

              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onloadend = async () => {
                const imageUrl = reader.result as string;

                if (imageMaxWidth && imageMaxHeight) {
                  const isResolutionValid = await validateImageResolution(imageUrl, imageMaxWidth, imageMaxHeight);

                  if (!isResolutionValid) {
                    handleShowError?.(true);
                    resolve();
                    return;
                  }
                }

                validFiles.push(file as File);
                resolve();
              };
            }),
        ),
      );
      if (validFiles.length > 0) {
        onChange?.(validFiles);
      }
    }, 500),
    [handleShowError, imageMaxHeight, imageMaxSize, imageMaxWidth, onChange, t, acceptance],
  );

  return (
    <StyledDragger
      beforeUpload={(file, fileList) => {
        handleSelectFiles(fileList);
        return false;
      }}
      maxCount={maxCount}
      multiple={maxCount > 1}
      showUploadList={showUploadList}
      accept={acceptance}
      {...props}
    >
      {children}
    </StyledDragger>
  );
};

export default PureImageUploader;
